import Pagination from "@mui/material/Pagination";
import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { PiArrowsDownUpFill } from "react-icons/pi";
import SearchInput from "../../components/ui/SearchInput";
import { showToastError, showToastSuccess } from "../../utils/common/ToastMessages";
import { getLookUPData } from "../../services/ResourcesService";
import { getSurveyList, SaveSurveyDetails } from "../../services/SurveyService";
import { fetchImages } from "../../constants/HandleImages";
import { ErrorMessages } from "../../constants/Labels";
import { FaEdit } from "react-icons/fa";

type SortColumn = "id" | "name";

/* ---------- INTERFACES ---------- */

interface UserType {
    TypeId: number;
    UserType: string;
    DisplayPosition: number;
    IsSkip: number;
}

interface SurveyType {
    SurveyTypeId: number;
    TypeName: string;
}

interface Survey {
    Id: number;
    SurveyId: string;
    SurveyName: string;
    SurveyLink: string;
    UserTypes: number[];
    SurveyTypeId: number | null;
    IsActive: boolean;
}

interface BadgeListProps {
  items: string[];
  visibleCount?: number;
  title?: string;
}

/* ---------- COMPONENT ---------- */

export default function SurveyList() {
    const recordsPerPage = 10;

    const [pageNumber, setPageNumber] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>("id");
    const [sortOrder, setSortOrder] = useState<"Asc" | "Desc">("Asc");

    const [surveyData, setSurveyData] = useState<Survey[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [userTypes, setUserTypes] = useState<UserType[]>([]);
    const [surveyTypes, setSurveyTypes] = useState<SurveyType[]>([]);
    const [showModal, setShowModal] = useState(false);
const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
const [checkedUserTypes, setCheckedUserTypes] = useState<Record<string, boolean>>({});


    /* ---------- FETCH LOOKUPS ---------- */
    useEffect(() => {
        getLookUPData()
            .then((res: any) => {
                setUserTypes(res?.[0]?.UserTypes || []);
                setSurveyTypes(res?.[0]?.SurveyTypes || []);
            })
            .catch(() => showToastError("Error while getting lookup data"));
    }, []);

    /* ---------- FETCH SURVEY LIST ---------- */
    useEffect(() => {
        if (userTypes.length > 0) {
            getSurveyListData();
        }
    }, [userTypes]);

    const getSurveyListData = () => {
        setIsLoading(true)
        getSurveyList()
            .then((response: any) => {
                // response is already an array, map directly
                const allowedUserTypeIds = new Set(
                    userTypes.filter(u => u.IsSkip === 0).map(u => u.TypeId)
                );

                const mappedData: Survey[] = response.map((item: any) => ({
                    Id: item.Id,
                    SurveyId: item.SurveyId,
                    SurveyName: item.SurveyName,
                    SurveyLink: item.SurveyLink,
                    IsActive: item.IsActive,

                    // Convert comma-separated string to number array, filter by allowed IDs
                    UserTypes:
                        item.LUUserTypeID_FK && item.LUUserTypeID_FK !== "0"
                            ? item.LUUserTypeID_FK
                                .split(",")
                                .map(Number)
                                .filter((id: number) => allowedUserTypeIds.has(id))
                            : [],

                    // SurveyTypeId is a single number
                    SurveyTypeId: item.LUSurveyTypeID_FK || null
                }));

                setSurveyData(mappedData);
            })
            .catch(() => showToastError(ErrorMessages.SomethingWrong))
            .finally(() => setIsLoading(false));
    };

    /* ---------- OPTIONS ---------- */
    const userTypeOptions = useMemo(
        () =>
            userTypes
                .filter(u => u.IsSkip === 0)
                .sort((a, b) => a.DisplayPosition - b.DisplayPosition)
                .map(u => ({
                    label: u.UserType,
                    value: u.TypeId
                })),
        [userTypes]
    );

    const surveyTypeOptions = useMemo(
        () =>
            surveyTypes.map(st => ({
                label: st.TypeName,
                value: st.SurveyTypeId
            })),
        [surveyTypes]
    );

    /* ---------- FILTER + SORT ---------- */
    const processedData = useMemo(() => {
        let data = [...surveyData];

        if (searchTerm) {
            data = data.filter(d =>
                d.SurveyName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        data.sort((a, b) => {
            if (sortColumn === "name") {
                return sortOrder === "Asc"
                    ? a.SurveyName.localeCompare(b.SurveyName)
                    : b.SurveyName.localeCompare(a.SurveyName);
            }
            return sortOrder === "Asc" ? a.Id - b.Id : b.Id - a.Id;
        });

        return data;
    }, [surveyData, searchTerm, sortColumn, sortOrder]);

    /* ---------- PAGINATION ---------- */
    const totalPages = Math.ceil(processedData.length / recordsPerPage);

    const paginatedData = useMemo(() => {
        const start = (pageNumber - 1) * recordsPerPage;
        return processedData.slice(start, start + recordsPerPage);
    }, [processedData, pageNumber]);

    const toggleOrder = (column: SortColumn) => {
        setSortColumn(column);
        setSortOrder(prev =>
            sortColumn === column && prev === "Asc" ? "Desc" : "Asc"
        );
        setPageNumber(1);
    };

    /* ---------- UPDATE ---------- */
    const handleRowUpdate = async (row: Survey) => {
        // ---------- VALIDATION ----------
        // if (!row.UserTypes || row.UserTypes.length === 0) {
        //     showToastError("Please select at least one User Type");
        //     return;
        // }

        if (!row.SurveyTypeId) {
            showToastError("Please select a Survey Type");
            return;
        }

        // ---------- PAYLOAD ----------
        const payload = {
            id: row.Id,
            userTypeID: row.UserTypes.join(","), // already validated
            surveyTypeID: row.SurveyTypeId
        };
        try {
            const res: any = await SaveSurveyDetails(payload);
            if (res?.status) {
                getSurveyListData();
                showToastSuccess(res?.message);
            } else {
                showToastError(res?.message);
            }
        } catch {
            showToastError(ErrorMessages.SomethingWrong);
        }
    };

    const openEditPopup = (survey: Survey) => {
    const initialChecks: Record<string, boolean> = {};

    userTypes.forEach(u => {
        initialChecks[u.UserType] = survey.UserTypes.includes(u.TypeId);
    });

    setCheckedUserTypes(initialChecks);
    setSelectedSurvey({ ...survey });
    setShowModal(true);
};

const handleUserTypeChange = (type: UserType) => {
    setCheckedUserTypes(prev => ({
        ...prev,
        [type.UserType]: !prev[type.UserType]
    }));
};

const handlePopupSave = async () => {
    if (!selectedSurvey) return;

    const selectedIds = userTypes
        .filter(u => checkedUserTypes[u.UserType])
        .map(u => u.TypeId);

    if (!selectedSurvey.SurveyTypeId) {
        showToastError("Please select Survey Type");
        return;
    }

    const payload = {
        id: selectedSurvey.Id,
        userTypeID: selectedIds.join(","),
        surveyTypeID: selectedSurvey.SurveyTypeId
    };

    try {
        const res: any = await SaveSurveyDetails(payload);
        if (res?.status) {
            getSurveyListData();
            showToastSuccess(res.message);
            setShowModal(false);
            
        } else {
            showToastError(res.message);
        }
    } catch {
        showToastError(ErrorMessages.SomethingWrong);
    }
};



const BadgeList: React.FC<BadgeListProps> = ({
  items,
  visibleCount = 2,
  title = "All Items",
}) => {
  const [open, setOpen] = useState(false);

  const visibleItems = items.slice(0, visibleCount);
  const remainingCount = items.length - visibleCount;

  return (
    <>
      <div className="relative inline-flex items-center gap-2 group">
        {visibleItems.map((item, index) => (
  <div
    key={index}
    className="text-sm text-gray-800 flex items-center justify-center whitespace-nowrap"
  >
    <span className="block lh-[16px]">
      {item}
      {index < visibleItems.length - 1 && ", "}
    </span>
  </div>
))}
 
        {remainingCount > 0 && (
          <button
            onClick={() => setOpen(true)}
            className=" text-sm bg-gray-200 rounded-full hover:bg-gray-300"
          >
            ...
          </button>
        )}
      

     {remainingCount > 0 && (
        <div className="absolute top-full right-0 top-[10px] mt-2 hidden rounded-[10px] shadow-lg group-hover:block z-50 bg-[rgba(0,0,0,0.5)]" onClick={() => setOpen(false)}>
          <div className="bg-dark rounded-lg p-4 w-[240px]  border shadow-lg rounded-[10px]">
           

            <div className="flex flex-wrap gap-2">
              {items.map((item, index) => (
                <span
                  key={index}
                  className="px-3 block lh-[14px] py-1 text-[12px] bg-gray-100 text-white rounded-full border"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

    /* ---------- UI ---------- */
    return (
        <>
        {showModal && selectedSurvey && (
  <div
    className="modal fade show d-block"
    style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
  >
    <div className="modal-dialog modal-xl modal-dialog-centered">
      <div className="modal-content shadow-lg rounded-3">

        {/* ---------- HEADER ---------- */}
        <div className="modal-header px-4 py-3 border-bottom">
          <h5 className="modal-title fw-bold">Edit Survey</h5>
          <button
            className="btn-close"
            onClick={() => setShowModal(false)}
          />
        </div>

        {/* ---------- BODY ---------- */}
        <div
          className="modal-body px-4 py-3"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >

          {/* Survey Id & Name */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="fw-bold mb-1">Survey Id</label>
              <input
                className="form-control"
                value={selectedSurvey.SurveyId}
                disabled
              />
            </div>

            <div className="col-md-6">
              <label className="fw-bold mb-1">Survey Name</label>
              <input
                className="form-control bg-light"
                value={selectedSurvey.SurveyName}
                disabled
              />
            </div>
          </div>


<div>
  <label className="fw-bold mb-2 d-block">Survey Type</label>

  <div className="row g-3">
    {surveyTypes.map((type) => (
      <div key={type.SurveyTypeId} className="col-lg-3 col-md-4 col-sm-6">
        <label
          className="border rounded-3 p-3 w-100 d-flex align-items-center gap-2 cursor-pointer bg-light shadow-sm"
          style={{
            minHeight: "48px",
            justifyContent: "flex-start",
          }}
        >
          <input
            type="radio"
            name="surveyType"
            className="form-check-input"
            style={{
    margin: 0,
    verticalAlign: "middle",
  }}
            checked={selectedSurvey?.SurveyTypeId === type.SurveyTypeId}
            onChange={() =>
              setSelectedSurvey((prev) =>
                prev ? { ...prev, SurveyTypeId: type.SurveyTypeId } : prev
              )
            }
          />
          <span className="fw-medium"
          style={{
    marginTop: "3px",
    verticalAlign: "middle",
  }}
          >{type.TypeName}</span>
        </label>
      </div>
    ))}
  </div>
</div>


          {/* User Types */}
          <div className="mt-4">
            <label className="fw-bold mb-2 d-block">
              User Types
            </label>

            <div className="row g-3">
              {userTypes
                .filter(u => u.IsSkip === 0)
                .map(type => (
                  <div
                    key={type.TypeId}
                    className="col-lg-3 col-md-4 col-sm-6"
                  >
                    <label
                      className="border rounded-3 p-3 w-100 d-flex align-items-center gap-2 cursor-pointer bg-light shadow-sm"
                      style={{ minHeight: "48px" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        style={{
    margin: 0,
    verticalAlign: "middle",
  }}
                        checked={
                          checkedUserTypes[type.UserType] || false
                        }
                        onChange={() =>
                          setCheckedUserTypes(prev => ({
                            ...prev,
                            [type.UserType]: !prev[type.UserType]
                          }))
                        }
                      />
                      <span className="fw-medium" 
                      style={{
    marginTop: "3px",
    verticalAlign: "middle",
  }}
                      >
                        {type.UserType}
                      </span>
                    </label>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* ---------- FOOTER ---------- */}
        <div className="modal-footer px-4 py-3 border-top">
          <button
            className="btn btn-outline-secondary px-4"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary px-4"
            onClick={handlePopupSave}
          >
            Save Changes
          </button>
        </div>

      </div>
    </div>
  </div>
)}

        <div className="survey-list p-2">
            <div className="shadow-sm bg-white rounded border p-3 mb-2">
                <h3 className="fw-bold mb-0">Survey List</h3>
            </div>
            <div className="d-flex justify-content-end p-2 mt-0 bg-white align-items-center mb-2">
                <div className="me-3">
                    <SearchInput
                        label="Search by Name"
                        value={searchTerm}
                        onChange={(v) => {
                            setSearchTerm(v);
                            setPageNumber(1);
                        }}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center bg-white rounded shadow-sm border" style={{ minHeight: 300 }}>
                    <img src={fetchImages.loader} alt="Loading..." style={{ width: 50, height: 'auto' }} />
                </div>
            ) : (
            <>
            <div className="shadow-sm border rounded bg-white mt-2">
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                    <table className="table mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                        <thead>
                            <tr className="fw-bold sticky top-0 bg-white" style={{ zIndex: 10 }}>
                                <th style={{ width: 140, cursor: "pointer" }} onClick={() => toggleOrder("id")}>
                                    <div className="d-flex align-items-center justify-content-start">
                                        <span>Survey Id</span>
                                        <PiArrowsDownUpFill size={14} className="ms-1" />
                                    </div>
                                </th>
                                <th style={{ width: 240, cursor: "pointer" }} onClick={() => toggleOrder("name")}>
                                    <div className="d-flex align-items-center justify-content-start">
                                        <span>Name</span>
                                        <PiArrowsDownUpFill size={14} className="ms-1" />
                                    </div>
                                </th>
                                <th style={{ width: 420 }}>User Types</th>
                                <th style={{ width: 100 }}>Survey Type</th>
                                <th style={{ width: 50 }}>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedData.length ? (
                                paginatedData.map(item => (
                                    <tr key={item.Id} className="align-middle"> {/* Ensures proper vertical alignment */}
                                        <td
                                            style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                verticalAlign: "middle" // Align text vertically center
                                            }}
                                            title={item.SurveyId}
                                        >
                                            {item.SurveyId}
                                        </td>

                                        <td
                                            style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                verticalAlign: "middle"
                                            }}
                                            title={item.SurveyName}
                                        >
                                            {item.SurveyName}
                                        </td>

                                      

<td
  className="!min-w-[500px]"
  style={{ verticalAlign: "middle" }}
>
  {item?.UserTypes?.length ? (
    <BadgeList
      visibleCount={3}
      title="User Types"
      items={item.UserTypes.map(
        id => userTypeOptions.find(u => u.value === id)?.label || "--"
      )}
    />
  ) : (
    <span>--</span>
  )}
</td>


                                        

<td
  className="w-[100px]"
  style={{ verticalAlign: "middle" }}
  title={
    surveyTypeOptions.find(
      opt => opt.value === item.SurveyTypeId
    )?.label || "--"
  }
>
  {(() => {
    const surveyType =
      surveyTypeOptions.find(
        opt => opt.value === item.SurveyTypeId
      )?.label || "--";

    const maxLength = 20;

    return surveyType.length > maxLength
      ? `${surveyType.slice(0, maxLength)}...`
      : surveyType;
  })()}
</td>




                                        <td style={{ verticalAlign: "middle" }}>
    <div className="d-flex gap-2">
        <button
            className="btn btn-outline-secondary"
            onClick={() => openEditPopup(item)}
        >
            <FaEdit />
        </button>

        {/* <button
            className="btn btn-primary text-white"
            onClick={() => handleRowUpdate(item)}
        >
            Update
        </button> */}
    </div>
</td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="text-center py-4 fw-bold">
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>

                    </table>
                </div>
            </div>

            <div className="d-flex justify-content-end mt-2">
                <Pagination
                    count={totalPages}
                    page={pageNumber}
                    onChange={(_, v) => setPageNumber(v)}
                    variant="outlined"
                    shape="rounded"
                    size="large"
                />
            </div>
            </>
            )}
        </div>
    </>
    );
}